import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { IoMdArrowRoundBack } from "react-icons/io";
import { MdEdit } from "react-icons/md";

import {
  getAllCompanyUsers,
  updateComapnyUsers,
} from "../../Services/userService";
import { Spinner, AccessDenied } from "../../Components/Common";
import { Pagination } from "./Components";
import {
  USER_COLUMNS,
  USER_FORM_LABELS,
  TOAST_MESSAGES,
  ROLES_ARRAY,
  STATUS_OPTIONS,
} from "./constants";
import { UserContext } from "../../Context/UserContext";
import { FiSearch, FiX } from "react-icons/fi";

const UserList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAdmin } = useContext(UserContext);

  // Pagination states
  const [page, setPage] = useState(1);
  const limit = 10;
  const totalPages = Math.ceil(users.length / limit);
  const startIndex = (page - 1) * limit;
  const paginatedUsers = users.slice(startIndex, startIndex + limit);

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [search, setSearch] = useState("");

  const fetchUsers = async (searchValue) => {
    try {
      const res = await getAllCompanyUsers({ search: searchValue });
      setUsers(res.data.results || []);
    } catch (err) {
      toast.error(TOAST_MESSAGES.ERROR_FETCHING_USERS);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (isAdmin) {
      fetchUsers("");
    }
  }, [isAdmin]);

  //Debounced Search Effect (auto-search on change)
  useEffect(() => {
    const handler = setTimeout(() => {
      if (isAdmin) {
        setPage(1);
        fetchUsers(search.trim());
        setIsSearching(!!search.trim());
      }
    }, 300); // delay to avoid spamming API

    return () => clearTimeout(handler);
  }, [search, isAdmin]);

  const handleEdit = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedUser) return;
    setIsSubmitting(true);

    try {
      const res = await updateComapnyUsers(selectedUser.id, {
        role: selectedUser.role,
        is_approved: selectedUser.is_approved,
      });

      if (res.status === 200) {
        toast.success(TOAST_MESSAGES.USER_UPDATED);
        fetchUsers(search.trim());
        setIsEditModalOpen(false);
      } else {
        toast.error(TOAST_MESSAGES.USER_UPDATION_FAILED);
      }
    } catch {
      toast.error(TOAST_MESSAGES.SOMETHING_WENT_WRONG);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAdmin) return <AccessDenied />;

  if (loading) {
    return (
      <div className="flex flex-col items-start justify-start h-screen gap-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-center text-sm font-lg"
          >
            <IoMdArrowRoundBack size={20} className="text-secondary" />
          </button>
          <h1 className="text-secondary text-2xl font-semibold">
            Manage Users
          </h1>
        </div>
        <Spinner />
      </div>
    );
  }

  const clearSearch = () => {
    setSearch("");
    setPage(1);
    fetchUsers("");
    setIsSearching(false);
  };

  return (
    <div className="h-[80%]">
      {/* Header */}
      <div className="flex gap-4 justify-between items-center">
        <div className="flex gap-4 mt-2 ml-4 items-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-center text-sm font-lg"
          >
            <IoMdArrowRoundBack size={20} className="text-secondary" />
          </button>
          <h1 className="text-secondary text-2xl font-semibold">
            Manage Users
          </h1>
        </div>
        <div className="flex gap-4 mt-4 mr-4 items-center relative">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-36 md:w-64 px-4 py-2 rounded-lg border border-gray-300 shadow-sm
              focus:outline-none focus:ring-1 focus:ring-blue-500
              focus:border-blue-500 text-sm"
          />
          {search && (
            <button
              onClick={clearSearch}
              className="absolute top-2 right-2 text-gray-500 hover:text-blue-600 
                        bg-white text-sm font-medium transition-colors duration-200"
            >
              <FiX size={20} />
            </button>
          )}
          {!search && (
            <FiSearch
              size={20}
              className="absolute top-2 right-2 text-gray-400 pointer-events-none"
            />
          )}
        </div>
      </div>

      <div className="h-[68vh] md:h-[60vh] overflow-y-auto overflow-x-auto thin-scrollbar m-4">
        <table className="w-full text-sm text-left text-gray-500 border-collapse">
          <thead className="text-sm text-secondary bg-gray-50 sticky top-0 z-10">
            <tr>
              {USER_COLUMNS.map((col) => (
                <th key={col.key} className="px-6 py-3 border-b">
                  {col.label}
                </th>
              ))}
              <th className="px-6 py-3 border-b">Action</th>
            </tr>
          </thead>

          <tbody>
            {paginatedUsers.length > 0 ? (
              paginatedUsers.map((user) => (
                <tr
                  key={user.id}
                  className="bg-white border-b hover:bg-gray-50"
                >
                  <td className="px-6 py-4">
                    {user.first_name} {user.last_name}
                  </td>
                  <td className="px-6 py-4">{user.mobile || "-"}</td>
                  <td className="px-6 py-4">{user.company_name || "-"}</td>
                  <td className="px-6 py-4">{user.role || "-"}</td>
                  <td className="px-6 py-4">
                    {user.is_approved
                      ? STATUS_OPTIONS.ACTIVE
                      : STATUS_OPTIONS.DEACTIVE}
                  </td>
                  <td className="flex items-center px-6 py-4 gap-4">
                    <MdEdit
                      size={20}
                      className="cursor-pointer text-secondary"
                      title="Edit"
                      onClick={() => handleEdit(user)}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={USER_COLUMNS.length + 1}
                  className="px-6 py-4 text-center"
                >
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end mt-2 mb-12 mr-4">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg relative">
            <h2 className="text-lg font-semibold mb-4">Edit User</h2>

            <div className="flex flex-col gap-4">
              {/* Role Dropdown */}
              <div>
                <label className="block mb-1 font-medium">
                  {USER_FORM_LABELS.role}
                </label>
                <select
                  value={selectedUser.role}
                  onChange={(e) =>
                    setSelectedUser((prev) => ({
                      ...prev,
                      role: e.target.value,
                    }))
                  }
                  className="border rounded p-2 w-full"
                >
                  {ROLES_ARRAY.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Active Dropdown */}
              <div>
                <label className="block mb-1 font-medium">
                  {USER_FORM_LABELS.is_approved}
                </label>
                <select
                  value={selectedUser.is_approved}
                  onChange={(e) =>
                    setSelectedUser((prev) => ({
                      ...prev,
                      is_approved: e.target.value === "true",
                    }))
                  }
                  className="border rounded p-2 w-full"
                >
                  <option value="true">Active</option>
                  <option value="false">Deactive</option>
                </select>
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="bg-gray-300 px-4 py-2 rounded disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="bg-primary hover:bg-secondary text-white px-4 py-2 rounded disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? <Spinner /> : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
